import {
  Maybe,
  SanityBlockGraphDataPoint,
  SanityDonutGraphCard,
  SanityFundInvestmentBarGraphCard,
  SanityFundInvestmentBlockGraphCard,
  SanityFundInvestmentDualBlockGraphCard,
  SanityFundInvestmentPieGraphCard,
} from '../../types/generated/graphql-types'
import { GraphData } from '../components/Graphs/BlockGraph'
import { DonutGraphCardProps } from '../components/Graphs/DonutGraphCard'
import { InvestmentBarCardProps } from '../components/InvestmentBarCard'
import { InvestmentBlockCardProps } from '../components/InvestmentBlockCard'
import { InvestmentDualBlockCardProps } from '../components/InvestmentDualBlockCard'
import { InvestmentPieCardProps } from '../components/InvestmentPieCard'
import { notEmpty } from '../utilities/filters'

export const mapSanityInvestmentBarGraphCardToInvestmentBarGraphProps = (
  barGraphCard: SanityFundInvestmentBarGraphCard
): InvestmentBarCardProps => {
  const labels: string[] = []
  const series: number[] = []
  barGraphCard?.datapoints?.forEach(dp => {
    labels.push(dp?.label ?? '')
    series.push(dp?.value ?? 0)
  })

  return {
    heading: barGraphCard.heading ?? '',
    text: barGraphCard.text ?? '',
    data: {
      labels,
      series,
    },
    keyMetrics: {
      irr: barGraphCard.keyMetrics?.irr || 0,
      moic: barGraphCard.keyMetrics?.moic || 0,
    },
  }
}

const mapSanityBlockGraphDatapointsToGraphData = (
  datapoints: Maybe<SanityBlockGraphDataPoint>[] | undefined
): GraphData => {
  const labels: string[] = []
  const series: number[] = []
  datapoints?.forEach(dp => {
    labels.push(dp?.label ?? '')
    series.push(dp?.percentage ?? 0)
  })

  return {
    labels,
    series,
  }
}

export const mapSanityFundInvestmentBlockGraphCardToInvestmentBlockCardProps = (
  blockGraphCard: SanityFundInvestmentBlockGraphCard
): InvestmentBlockCardProps => ({
  heading: blockGraphCard.heading ?? '',
  subheading: blockGraphCard.subheading ?? '',
  text: blockGraphCard.text ?? '',
  data: mapSanityBlockGraphDatapointsToGraphData(blockGraphCard?.datapoints ?? undefined),
  keyMetrics: {
    irr: blockGraphCard.keyMetrics?.irr || 0,
    moic: blockGraphCard.keyMetrics?.moic || 0,
  },
})

export const mapSanityFundInvestmentDualBlockGraphCardToInvestmentDualBlockCardProps = (
  dualBlockGraphCard: SanityFundInvestmentDualBlockGraphCard
): InvestmentDualBlockCardProps => ({
  heading: dualBlockGraphCard.heading ?? '',
  leftCaption: dualBlockGraphCard?.leftCaption ?? '',
  leftData: mapSanityBlockGraphDatapointsToGraphData(
    dualBlockGraphCard?.leftDatapoints ?? undefined
  ),
  rightCaption: dualBlockGraphCard?.rightCaption ?? '',
  rightData: mapSanityBlockGraphDatapointsToGraphData(
    dualBlockGraphCard?.rightDatapoints ?? undefined
  ),
})

export const mapSanityFundInvestmentPieGraphCardToInvestmentPieCardProps = (
  pieGraphCard: SanityFundInvestmentPieGraphCard
): InvestmentPieCardProps => {
  const labels: string[] = []
  const series: number[] = []
  pieGraphCard?.datapoints?.forEach(dp => {
    labels.push(dp?.label ?? '')
    series.push(dp?.percentage ?? 0)
  })

  return {
    heading: pieGraphCard.heading ?? '',
    subheading: pieGraphCard.subheading ?? '',
    text: pieGraphCard.text ?? '',
    data: {
      labels,
      series,
    },
    keyMetrics: {
      irr: pieGraphCard.keyMetrics?.irr || 0,
      moic: pieGraphCard.keyMetrics?.moic || 0,
    },
  }
}

export const mapSanityDonutGraphCardToDonutGraphCardProps = ({
  additionalInfo,
  datapoints,
  footNote,
}: SanityDonutGraphCard): DonutGraphCardProps => ({
  additionalInfo: additionalInfo?.filter(notEmpty).map(item => ({
    label: item.label ?? '',
    value: item.value ?? '',
  })),
  data:
    datapoints?.filter(notEmpty).map(d => ({
      label: d.label ?? '',
      footnotes: d.footnotes?.filter(notEmpty) ?? undefined,
      value: d.percentage ?? 0,
    })) ?? [],
  footNote: footNote ?? '',
})
